import React, { memo } from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import { DialogProps } from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

// Styled Title
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
  paddingRight: theme.spacing(6),
  paddingLeft: theme.spacing(6),
  position: "relative",
  textAlign: "center",
  fontWeight: 700,
  fontFamily: "Montserrat, sans-serif",
  fontSize: 20,
  color: "var(--neutral-700)",
}));

// Styled Close Button
const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1),
  top: theme.spacing(2),
  color: theme.palette.grey[500],
}));

// Styled Content
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
}));

// Title Component with Close Button
interface RpDialogTitleProps {
  children?: React.ReactNode;
  id?: string;
  onClose?: () => void;
  persists?: boolean;
}

const RpDialogTitle = ({
  children,
  onClose,
  persists,
  ...other
}: RpDialogTitleProps) => (
  <StyledDialogTitle {...other}>
    {children}

    {onClose && !persists && (
      <CloseButton aria-label="close" onClick={onClose}>
        <CloseIcon />
      </CloseButton>
    )}
  </StyledDialogTitle>
);

// Content Wrapper
const RpDialogContent = (props: React.ComponentProps<typeof DialogContent>) => (
  <StyledDialogContent {...props} />
);

// Final Dialog
interface RpMUIDialogProps extends Omit<DialogProps, "onClose"> {
  open: boolean;
  onClose?: () => void;
  heading?: React.ReactNode;
  children?: React.ReactNode;
  persists?: boolean;
}

const RpMUIDialog = ({
  open,
  onClose,
  heading = "",
  children,
  persists = false,
  ...rest
}: RpMUIDialogProps) => {
  const handleOnClose = (_e?: object, reason?: string) => {
    if (persists && reason === "backdropClick") return;
    onClose?.();
  };

  return (
    <Dialog
      onClose={handleOnClose}
      aria-labelledby={`${heading || "rp-mui"}-dialog-title`}
      open={open}
      disableEscapeKeyDown={persists}
      fullWidth
      {...rest}
    >
      {heading && (
        <RpDialogTitle
          id={`${heading || "rp-mui"}-dialog-title`}
          persists={persists}
          onClose={handleOnClose}
        >
          {heading}
        </RpDialogTitle>
      )}
      <RpDialogContent dividers>{children}</RpDialogContent>
    </Dialog>
  );
};

export default memo(RpMUIDialog);
